import { useContext, useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import TemplatesIcon from './Icons/templates.svg';
import BrandKitIcon from './Icons/brandKit.svg';
import CatalogueManagementIcon from './Icons/catalogueManagement.svg';
import AdTagIcon from './Icons/adTag.svg';
import VideoTagIcon from './Icons/videoTag.svg';
import './index.css';
import { useFeatureFlags } from '@adflow/utils';
import { UserContext } from '@adflow/utils';

interface IRoute {
  title: string;
  icon: string;
  link: string;
  enabled: boolean;
}

export default function SideNavigation() {
  useFeatureFlags();
  const [isVideoTagsEnabled, setIsVideoTagsEnabled] = useState(false);
  const [isAdTagsEnabled, setIsAdTagsEnabled] = useState(false);
  const [isCataloguesEnabled, setIsCataloguesEnabled] = useState(false);
  const [isClosed, setIsClosed] = useState(
    localStorage.getItem('mc-navigation-state') === 'closed',
  );

  useEffect(() => {
    const navigationChangeEvt = (evt: MessageEvent) => {
      if (evt?.data?.key === 'mc-navigation-state') {
        if (evt.data.value === 'closed') setIsClosed(true);
        else setIsClosed(false);
      }
    };
    window.addEventListener('message', navigationChangeEvt);
    return () => {
      window.removeEventListener('message', navigationChangeEvt);
    };
  }, []);

  const { user } = useContext(UserContext);

  useEffect(() => {
    const checkVideoTagsEnabled = async () => {
      const canRead = await user?.canRead('creative-hub', 'video-tag');
      setIsVideoTagsEnabled(canRead ?? false);
    };
    checkVideoTagsEnabled();
    const checkAdTagsEnabled = async () => {
      const canRead = await user?.canRead('creative-hub', 'ad-tag');
      setIsAdTagsEnabled(canRead ?? false);
    };
    checkAdTagsEnabled();
    const checkCataloguesEnabled = async () => {
      const canRead = await user?.canRead('creative-hub', 'catalogues');
      setIsCataloguesEnabled(canRead ?? false);
    };
    checkCataloguesEnabled();
  }, [user]);

  const routes: IRoute[] = [
    {
      title: 'Templates',
      icon: TemplatesIcon,
      link: '/templates',
      enabled: isAdTagsEnabled || isCataloguesEnabled,
    },
    {
      title: 'Brand Kits',
      icon: BrandKitIcon,
      link: '/brand-kits',
      enabled: isAdTagsEnabled || isCataloguesEnabled,
    },
    {
      title: 'Catalogues',
      icon: CatalogueManagementIcon,
      link: '/catalogues',
      enabled: isCataloguesEnabled,
    },
    {
      title: 'Ad Tags',
      icon: AdTagIcon,
      link: '/ad-tags',
      enabled: isAdTagsEnabled,
    },
    {
      title: 'Video Tags',
      icon: VideoTagIcon,
      link: '/video-tags',
      enabled: isVideoTagsEnabled,
    },
  ];

  const renderRoute = (route: IRoute) => {
    return (
      <NavLink to={route.link} key={route.title}>
        <div className="sideBarItem">
          <div className="sideBarOverlay" />
          <div className="sideBarIcon_container">
            <img className="sideBarIcon" src={route.icon} alt="{route.title} icon" />
          </div>
          <div className="sideBarTitle">{route.title}</div>
        </div>
      </NavLink>
    );
  };

  const className = useMemo(() => (isClosed ? 'closed' : ''), [isClosed]);

  return (
    <div id="sidebar" className={className}>
      {routes.filter((route) => route.enabled).map(renderRoute)}
    </div>
  );
}
